import { useEffect, useRef, useState } from 'react';

const DEFAULT_DURATION = 900;

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

const prefersReducedMotion = () =>
    typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export default function useTween(target, duration = DEFAULT_DURATION) {
    const [current, setCurrent] = useState(target);
    const currentRef = useRef(target);
    const frameRef = useRef(null);

    useEffect(() => {
        if (currentRef.current === target || prefersReducedMotion()) {
            currentRef.current = target;
            setCurrent(target);
            return undefined;
        }

        const from = currentRef.current;
        const distance = target - from;
        const start = performance.now();

        const step = (timestamp) => {
            const progress = Math.min((timestamp - start) / duration, 1);
            const value = progress === 1
                ? target
                : from + distance * easeOutCubic(progress);

            currentRef.current = value;
            setCurrent(value);

            if (progress < 1) frameRef.current = requestAnimationFrame(step);
        };

        frameRef.current = requestAnimationFrame(step);

        return () => {
            if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
        };
    }, [target, duration]);

    return current;
}
